import React, { useState } from 'react';
import { Card, Table, DatePicker, Tag, Space, Button, ConfigProvider, theme } from 'antd';
import { FileExcelOutlined, PrinterOutlined, PieChartOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useAuthStore from '../../store/useAuthStore';
import { getConsumptionReport } from '../../api/reports.api';
import { formatQty, exportToCSV, formatDate } from '../../utils/formatters';

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-consumption-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-consumption-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }

    .light-consumption-page .ant-picker-sm,
    .light-consumption-page .ant-btn-sm {
      border-radius: 4px;
      font-size: 12px;
    }

    /* Table Header - Not pure white */
    .light-consumption-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-consumption-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    /* Print styling to hide controls */
    @media print {
      .no-print { display: none !important; }
      .light-consumption-page { padding: 0 !important; }
      .light-consumption-page .ant-card { border: none !important; box-shadow: none !important; }
    }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }
};

const ConsumptionReportPage = () => {
  const { user } = useAuthStore();
  const [dates, setDates] = useState([dayjs().startOf('month'), dayjs()]);

  const { data, isLoading } = useQuery({
    queryKey: ['consumptionReport', user.plantID, dates],
    queryFn: async () => {
      const res = await getConsumptionReport(user.plantID, { 
        from: dates[0].format('YYYY-MM-DD'), 
        to: dates[1].format('YYYY-MM-DD') 
      });
      if (res && Array.isArray(res.items)) return res.items;
      if (Array.isArray(res)) return res;
      return [];
    },
    enabled: !!user.plantID
  });

  const columns = [
    { title: 'Date', dataIndex: 'productionDate', key: 'productionDate', render: v => <span style={{ fontWeight: 500 }}>{formatDate(v)}</span> },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName', render: v => <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{v}</span> },
    { title: 'Expected Qty', dataIndex: 'expectedQty', key: 'expectedQty', render: v => <span style={{ color: 'var(--text-3)' }}>{formatQty(v)}</span> },
    { title: 'Actual Qty', dataIndex: 'consumptionQty', key: 'consumptionQty', render: v => <span style={{ fontWeight: 700 }}>{formatQty(v)}</span> },
    { title: 'Wastage', dataIndex: 'wastageQty', key: 'wastageQty', render: v => <span style={{ color: v > 0 ? '#ef4444' : 'var(--text-3)', fontWeight: v > 0 ? 600 : 400 }}>{formatQty(v)}</span> },
    { 
      title: 'Wastage %', 
      key: 'wastagePct',
      render: (_, r) => {
        const pct = r.consumptionQty > 0 ? (r.wastageQty / r.consumptionQty) * 100 : 0;
        let color = 'success';
        if (pct > 5) color = 'error';
        else if (pct > 2) color = 'warning';
        
        return (
          <Tag bordered={false} color={color} style={{ fontWeight: 600 }}>
            {pct.toFixed(2)}%
          </Tag>
        );
      }
    }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-consumption-page" style={{ padding: '8px 16px' }}>
        
        <Card
          className="report-card"
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PieChartOutlined style={{ color: 'var(--text-3)' }} />
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)' }}>
                Consumption & Wastage Analysis
              </span>
            </div>
          }
          extra={
            <Space className="no-print">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
              <Button size="small" type="primary" style={{ background: '#10b981' }} icon={<FileExcelOutlined />} onClick={() => exportToCSV(data || [], columns, `Consumption_Report_${dates[0].format('MMM-YYYY')}`)}>
                Export CSV
              </Button>
            </Space>
          }
        >
          {/* CONTROLS SECTION */}
          <div className="compact-section no-print" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>DATE RANGE :</span>
            <DatePicker.RangePicker 
              size="small" 
              value={dates} 
              onChange={setDates} 
              format="DD MMM YYYY" 
              allowClear={false} 
            />
          </div>

          {/* DATA TABLE */}
          <div style={S.tableWrap}>
            <Table 
              dataSource={data || []} 
              columns={columns} 
              loading={isLoading} 
              rowKey={(record, index) => `${record.productionDate}-${record.rawName}-${index}`}
              size="small"
              pagination={{ pageSize: 15, size: 'small' }}
              locale={{ emptyText: "No consumption records found for the selected dates." }}
              scroll={{ x: 600 }}
            />
          </div>

        </Card>

      </div>
    </ConfigProvider>
  );
};

export default ConsumptionReportPage;